<template>
  <StatusPill
    v-if="mode === 'overall' && display"
    :variant="display.variant"
    :icon="display.icon"
    :icon-type="display.iconType"
    :label="display.label"
  />
  <span
    v-else-if="mode === 'approvalSlot' && display"
    class="approval-status-slot d-inline-flex align-center"
    :class="display.slotClass"
  >
    <v-icon
      :icon="display.icon"
      size="14"
    />
    {{ display.label }}
  </span>
  <span
    v-else
    class="approval-status-empty text-textLight"
  >—</span>
</template>

<script setup>
import { computed } from 'vue';
import StatusPill from '@/components/shared/StatusPill.vue';

const APPROVAL_STATUS_CONFIG = {
  approved: {
    label: 'Approved',
    icon: 'mdi-check-circle-outline',
    variant: 'approval-approved',
    slotClass: 'approval-slot-approved',
  },
  pending: {
    label: 'Pending',
    icon: 'mdi-dots-horizontal-circle-outline',
    variant: 'approval-pending',
    slotClass: 'approval-slot-pending',
  },
  rejected: {
    label: 'Rejected',
    icon: 'mdi-close-circle-outline',
    variant: 'approval-rejected',
    slotClass: 'approval-slot-rejected',
  },
  cancelled: {
    label: 'Cancelled',
    icon: 'mdi-minus-circle-outline',
    variant: 'approval-cancelled',
    slotClass: 'approval-slot-cancelled',
  },
  queued: {
    label: 'Queued',
    icon: 'mdi-checkbox-blank-circle-outline',
    variant: null,
    slotClass: 'approval-slot-queued',
  },
};

const normalizeApprovalFlowStatus = (status) => (
  String(status || '').toLowerCase()
);

const normalizeApprovalSlotStatus = (status) => {
  const key = String(status || '').toLowerCase();

  if (key === 'staged') {
    return 'queued';
  }

  return key;
};

const getApprovalStatusDisplay = (status, mode = 'overall') => {
  const normalized = mode === 'approvalSlot'
    ? normalizeApprovalSlotStatus(status)
    : normalizeApprovalFlowStatus(status);

  const config = APPROVAL_STATUS_CONFIG[normalized];

  if (!config) {
    return null;
  }

  if (mode === 'overall' && !config.variant) {
    return null;
  }

  return {
    label: config.label,
    icon: config.icon,
    iconType: 'mdi',
    variant: config.variant,
    slotClass: config.slotClass,
  };
};

const props = defineProps({
  status: {
    type: String,
    default: null,
  },
  mode: {
    type: String,
    default: 'overall',
    validator: (value) => ['overall', 'approvalSlot'].includes(value),
  },
});

const display = computed(() => (
  getApprovalStatusDisplay(props.status, props.mode)
));
</script>

<style scoped lang="less">
.approval-status-slot {
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16.5px;
}

.approval-slot-approved {
  color: @teal-500;
}

.approval-slot-pending {
  color: @yellow-700;
}

.approval-slot-rejected {
  color: @red-700;
}

.approval-slot-queued,
.approval-slot-cancelled {
  color: @grey-800;
}
</style>
